export interface WorkbookStepperSection {
    id: string
    label: string
    required: boolean
    available?: boolean
    complete?: boolean
    editable?: boolean
}

export function sectionIsAvailable(section: WorkbookStepperSection): boolean {
    return section.available !== false
}

export function sectionIsComplete(section: WorkbookStepperSection): boolean {
    return Boolean(section.complete)
}

export function sectionIsEditable(section: WorkbookStepperSection): boolean {
    if (section.editable === true) return true
    if (section.editable === false) return false
    return sectionIsAvailable(section) && !sectionIsComplete(section)
}

export function firstOpenSectionIndex(
    sections: WorkbookStepperSection[],
): number {
    const incomplete = sections.findIndex(
        (section) => sectionIsAvailable(section) && !sectionIsComplete(section),
    )
    if (incomplete >= 0) return incomplete
    for (let index = sections.length - 1; index >= 0; index -= 1) {
        if (sectionIsAvailable(sections[index])) return index
    }
    return 0
}

export function nextAvailableIndex(
    sections: WorkbookStepperSection[],
    from: number,
): number {
    for (let index = from + 1; index < sections.length; index += 1) {
        if (sectionIsAvailable(sections[index])) return index
    }
    return from
}

export function prevAvailableIndex(
    sections: WorkbookStepperSection[],
    from: number,
): number {
    for (let index = from - 1; index >= 0; index -= 1) {
        if (sectionIsAvailable(sections[index])) return index
    }
    return from
}

export function isLastAvailableSection(
    sections: WorkbookStepperSection[],
    index: number,
): boolean {
    return nextAvailableIndex(sections, index) === index
}

export function joinSectionLabels(labels: string[]): string {
    if (labels.length === 0) return ''
    if (labels.length === 1) return labels[0]
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

export function requiredSectionsArePrefix(
    sections: WorkbookStepperSection[],
): boolean {
    const requiredCount = sections.filter((section) => section.required).length
    const optionalCount = sections.length - requiredCount
    if (requiredCount === 0 || optionalCount === 0) return false
    let seenOptional = false
    for (const section of sections) {
        if (!section.required) {
            seenOptional = true
        } else if (seenOptional) {
            return false
        }
    }
    return true
}

export function lastRequiredIndex(
    sections: WorkbookStepperSection[],
): number {
    return sections.reduce(
        (found, section, index) => (section.required ? index : found),
        -1,
    )
}

export function requiredProgress(
    sections: WorkbookStepperSection[],
): {
    required: WorkbookStepperSection[]
    completeCount: number
    headline: string | null
    countLabel: string | null
} {
    const required = sections.filter((section) => section.required)
    const completeCount = required.filter(sectionIsComplete).length
    if (required.length === 0) {
        return {
            required,
            completeCount,
            headline: null,
            countLabel: null,
        }
    }
    const allComplete = completeCount === required.length
    const labels = required.map((section) => section.label)
    const headline = allComplete
        ? 'Required sections are complete.'
        : labels.length > 4
          ? 'Complete the required sections to continue.'
          : `Complete ${joinSectionLabels(labels)} to continue.`
    return {
        required,
        completeCount,
        headline,
        countLabel: `${completeCount} of ${required.length} required complete`,
    }
}

export function groupActiveStep(
    activeStep: number,
    startIndex: number,
    length: number,
): number {
    const relative = activeStep - startIndex
    if (relative < 0) return -1
    if (relative >= length) return length
    return relative
}
